<template>
  <div class="oc-step-salary">
    <div v-if="!embedded" class="oc-step-header oc-step-header--compact mb-50">
      <div class="oc-step-header__icon oc-step-header__icon--compact">
        <feather-icon icon="DollarSignIcon" size="16" />
      </div>
      <div class="oc-step-header__body">
        <span class="oc-step-header__title">Salary table</span>
        <span class="oc-step-header__hint">
          · Annexure rows, headers &amp; footnotes (yearly = monthly × 12)
        </span>
      </div>
    </div>
    <div v-else class="oc-subsection-head mb-50">
      <feather-icon icon="DollarSignIcon" size="14" class="mr-50" />
      Salary table
    </div>

    <b-alert
      v-if="!salarySectionIncluded"
      show
      variant="light"
      class="mb-50 oc-salary-off-alert"
    >
      <feather-icon icon="ToggleLeftIcon" size="16" class="mr-50 text-muted" />
      Salary annexure is turned off. Enable
      <strong>Section 5 — Salary Annexure</strong> on the Write Letter step to include it in the PDF.
    </b-alert>

    <div class="oc-form-section mb-50" :class="{ 'oc-form-section--disabled': !salarySectionIncluded }">
      <div class="oc-form-section__head mb-75">
        <feather-icon icon="SlidersIcon" size="15" class="mr-50" />
        Section layout
      </div>
      <offer-section-page-controls
        section-key="salary"
        :page-start-mode="sectionPageStartMode('salary')"
        :font-size-px="sectionLayoutResolved('salary').fontSizePx"
        :section-title-text="sectionLayoutResolved('salary').sectionTitleText"
        :section-title-align="sectionLayoutResolved('salary').sectionTitleAlign || 'center'"
        :section-title-font-size-px="sectionLayoutResolved('salary').sectionTitleFontSizePx || 11"
        :section-title-font-weight="sectionLayoutResolved('salary').sectionTitleFontWeight || 'bold'"
        :default-section-title="defaultSectionTitleText.salary"
        supports-title
        :disabled="editorDisabled || !salarySectionIncluded"
        @page-start-change="$emit('page-start-change', 'salary', $event)"
        @font-size-change="$emit('section-layout-change', 'salary', 'fontSizePx', $event)"
        @section-title-change="$emit('section-title-change', 'salary', $event)"
        @section-title-font-size-change="$emit('section-title-font-size-change', 'salary', $event)"
        @section-title-font-weight-change="$emit('section-title-font-weight-change', 'salary', $event)"
        @section-title-align-change="$emit('section-title-align-change', 'salary', $event)"
      />
    </div>

    <b-alert
      v-if="salaryParseWarning"
      show
      variant="warning"
      class="mb-50 py-75 px-1"
    >
      <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
      <small class="mb-0">{{ salaryParseWarning }}</small>
    </b-alert>

    <div class="oc-form-section" :class="{ 'oc-form-section--disabled': !salarySectionIncluded }">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-75">
        <div class="oc-form-section__head mb-0">
          <feather-icon icon="GridIcon" size="15" class="mr-50" />
          Salary structures
          <b-badge
            v-if="structures.length > 1"
            variant="light-primary"
            class="ml-50"
          >
            {{ structures.length }} tables
          </b-badge>
        </div>
        <b-button
          v-if="!editorDisabled && salarySectionIncluded"
          size="sm"
          variant="outline-primary"
          class="oc-add-structure-btn mt-50 mt-sm-0"
          @click="$emit('add-structure')"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-25" />
          Add structure
        </b-button>
      </div>

      <p class="text-muted small mb-75">
        Add multiple named tables on the same Annexure A (e.g. base CTC and a higher-meter variant).
        Footnotes are shared below all tables.
      </p>

      <div
        v-if="structures.length > 1"
        class="oc-structure-tabs mb-75"
      >
        <b-nav pills class="oc-structure-nav flex-wrap">
          <b-nav-item
            v-for="(block, index) in structures"
            :key="`struct-tab-${index}`"
            :active="index === activeStructureIndex"
            href="#"
            @click.prevent="$emit('active-structure-change', index)"
          >
            {{ structureTabLabel(block, index) }}
          </b-nav-item>
        </b-nav>
      </div>

      <div
        v-for="(block, index) in structures"
        v-show="index === activeStructureIndex"
        :key="`struct-panel-${index}`"
        class="oc-structure-panel"
      >
        <div
          v-if="structures.length > 1"
          class="d-flex flex-wrap align-items-center justify-content-between mb-75"
        >
          <span class="small font-weight-bold text-secondary">
            Structure {{ index + 1 }} of {{ structures.length }}
          </span>
          <b-button
            v-if="!editorDisabled && salarySectionIncluded"
            size="sm"
            variant="outline-danger"
            @click="$emit('remove-structure', index)"
          >
            <feather-icon icon="Trash2Icon" size="14" class="mr-25" />
            Remove structure
          </b-button>
        </div>

        <offer-salary-annexure-editor
          :value="editorValueForStructure(index)"
          :disabled="editorDisabled || !salarySectionIncluded"
          :structure-mode="structures.length > 1"
          :show-heading="true"
          :show-notes="structures.length === 1"
          :heading-input-id="`oc-salary-heading-${index}`"
          @input="onStructureEditorInput(index, $event)"
          @add-row="onAddRow(index)"
          @remove-row="onRemoveRow(index, $event)"
          @move-row="onMoveRow"
        />
      </div>

      <!-- Shared footnotes when multiple structures -->
      <div
        v-if="structures.length > 1"
        class="oc-shared-notes mt-1"
      >
        <h6 class="oc-footnotes-title mb-75">
          <feather-icon icon="FileTextIcon" size="14" class="mr-50" />
          Shared footnotes (below all tables)
        </h6>
        <offer-salary-annexure-editor
          :value="notesOnlyEditorValue"
          :disabled="editorDisabled || !salarySectionIncluded"
          :structure-mode="false"
          :show-heading="false"
          :show-column-labels="false"
          :show-rows="false"
          :show-notes="true"
          heading-input-id="oc-salary-notes-only"
          @input="onNotesOnlyInput"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BNav,
  BNavItem,
} from 'bootstrap-vue'
import OfferSectionPageControls from '../OfferSectionPageControls.vue'
import OfferSalaryAnnexureEditor from '../OfferSalaryAnnexureEditor.vue'
import { normalizeSalaryStructureData } from '@/utils/offer-template-editor'

export default {
  name: 'OfferConfigureStepSalary',
  components: {
    BAlert,
    BBadge,
    BButton,
    BNav,
    BNavItem,
    OfferSectionPageControls,
    OfferSalaryAnnexureEditor,
  },
  props: {
    salarySectionIncluded: {
      type: Boolean,
      default: false,
    },
    salaryParseWarning: {
      type: String,
      default: '',
    },
    salaryEditor: {
      type: Object,
      required: true,
    },
    activeStructureIndex: {
      type: Number,
      default: 0,
    },
    defaultSectionTitleText: {
      type: Object,
      required: true,
    },
    editorDisabled: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    sectionLayoutResolved: {
      type: Function,
      required: true,
    },
    sectionPageStartMode: {
      type: Function,
      required: true,
    },
  },
  computed: {
    structures() {
      const normalized = normalizeSalaryStructureData(this.salaryEditor)
      return normalized.structures || []
    },
    notesOnlyEditorValue() {
      const normalized = normalizeSalaryStructureData(this.salaryEditor)
      return {
        heading: normalized.heading,
        title: '',
        columnLabels: normalized.columnLabels,
        rows: [{ component: '', monthly: '', yearly: '', rowType: 'normal' }],
        notes: normalized.notes,
      }
    },
  },
  methods: {
    structureTabLabel(block, index) {
      const title = String(block && block.title != null ? block.title : '').trim()
      if (title) return title.length > 28 ? `${title.slice(0, 26)}…` : title
      return `Structure ${index + 1}`
    },
    editorValueForStructure(index) {
      const normalized = normalizeSalaryStructureData(this.salaryEditor)
      const block = normalized.structures[index] || { title: '', columnLabels: {}, rows: [] }
      if (normalized.structures.length === 1) {
        // Single-structure: keep annexure heading in the editor field (legacy UX).
        return {
          heading: normalized.heading,
          title: block.title || '',
          columnLabels: block.columnLabels,
          rows: block.rows,
          notes: normalized.notes,
        }
      }
      return {
        heading: normalized.heading,
        title: block.title || '',
        columnLabels: block.columnLabels,
        rows: block.rows,
        notes: normalized.notes,
      }
    },
    onStructureEditorInput(index, partial) {
      const normalized = normalizeSalaryStructureData(this.salaryEditor)
      const structures = normalized.structures.map((block, i) => {
        if (i !== index) return block
        return {
          title: partial.title != null ? String(partial.title) : (block.title || ''),
          columnLabels: partial.columnLabels || block.columnLabels,
          rows: Array.isArray(partial.rows) ? partial.rows : block.rows,
        }
      })
      const next = {
        ...normalized,
        structures,
        notes: partial.notes && normalized.structures.length === 1
          ? partial.notes
          : normalized.notes,
      }
      // Single structure: heading field still drives annexure title.
      if (normalized.structures.length === 1 && partial.heading != null) {
        next.heading = partial.heading
      }
      this.$emit('salary-input', next)
    },
    onNotesOnlyInput(partial) {
      const normalized = normalizeSalaryStructureData(this.salaryEditor)
      this.$emit('salary-input', {
        ...normalized,
        notes: partial.notes || normalized.notes,
      })
    },
    onAddRow(structureIndex) {
      this.$emit('add-row', structureIndex)
    },
    onRemoveRow(structureIndex, rowIndex) {
      this.$emit('remove-row', structureIndex, rowIndex)
    },
    onMoveRow(rowIndex, direction) {
      // Method listener keeps `this` and receives both emit args (Vue 2).
      // Active panel is the only visible/interactive structure (v-show).
      this.$emit('move-row', this.activeStructureIndex, rowIndex, direction)
    },
  },
}
</script>

<style scoped>
.oc-form-section {
  background: #f8f9fb;
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
}

.oc-form-section--disabled {
  opacity: 0.65;
  pointer-events: none;
}

.oc-form-section__head {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2c2c2c;
}

.oc-salary-off-alert {
  border: 1px dashed #d8d6de;
  display: flex;
  align-items: center;
}

.oc-subsection-head {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2c2c2c;
}

.oc-add-structure-btn {
  font-weight: 600;
  white-space: nowrap;
}

.oc-structure-nav {
  gap: 0.25rem;
}

.oc-structure-nav >>> .nav-link {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35rem 0.65rem;
}

.oc-structure-panel {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 0.45rem;
  padding: 0.75rem;
}

.oc-shared-notes {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 0.45rem;
  padding: 0.75rem;
}

.oc-footnotes-title {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2c2c2c;
}
</style>
